import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { FormCheck } from "react-bootstrap";
import { Link } from "react-router-dom";
import { usePersistStore } from "../store";

interface ConfirmModalProps {
  show: boolean;
  onNo: () => void;
  onYes: (e: any) => void;
  title?: string;
  body?: string;
  closeButtonText?: string;
  confirmButtonText?: string;
  showIcon?: boolean;
}

const ConfirmShippingPopup: React.FC<ConfirmModalProps> = ({
  show,
  onNo,
  onYes,
  title = "Confirm Shipping",
  closeButtonText = "Cancel",
  confirmButtonText = "Start Shipping",
}) => {
  const [isAgreed, setIsAgreed] = useState(false);
  const { user } = usePersistStore((s: any) => s);

  const addressLine2 = [user?.city, user?.state, user?.zip]
    .filter(Boolean)
    .join(", ");
  const hasAddress = Boolean(
    user?.address1 || user?.address2 || addressLine2
  );

  return (
    <Modal
      show={show}
      onHide={onNo}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      dialogClassName="confirmModal"
    >
      <Modal.Body>
        <div className="confirmModal__icon">
          <i className="bi bi-truck" aria-hidden="true" />
        </div>
        <h2 className="confirmModal__title">{title}</h2>
        <p className="confirmModal__subtitle">
          Hand off your artwork to our logistics partner. Review the details
          below before confirming.
        </p>

        <div className="confirmModal__details">
          <div className="confirmModal__detailRow">
            <i className="bi bi-box-seam" aria-hidden="true" />
            <span>
              <strong>Arta</strong> handles pickup, transit, and delivery. They
              are fully insured.
            </span>
          </div>
          <div className="confirmModal__detailRow">
            <i className="bi bi-clock-history" aria-hidden="true" />
            <span>
              Their team will contact you within 24 hours to schedule the
              pickup.
            </span>
          </div>
          <div className="confirmModal__detailRow">
            <i className="bi bi-shield-check" aria-hidden="true" />
            <span>
              A tracking link will appear on this page once the shipment is
              created.
            </span>
          </div>
        </div>

        <div className="confirmModal__pickup">
          <div className="confirmModal__pickupHeader">
            <div className="confirmModal__pickupTitleBlock">
              <i className="bi bi-geo-alt" aria-hidden="true" />
              <div>
                <p className="confirmModal__pickupLabel">Pickup address</p>
                <p className="confirmModal__pickupHint">
                  This is the address Arta will collect from.
                </p>
              </div>
            </div>
            <Link
              to="/dashboard/profile"
              className="confirmModal__pickupEdit"
              onClick={onNo}
            >
              <i className="bi bi-pencil" aria-hidden="true" />
              <span>Edit</span>
            </Link>
          </div>
          {hasAddress ? (
            <div className="confirmModal__pickupBody">
              {user?.address1 ? <div>{user.address1}</div> : null}
              {user?.address2 ? <div>{user.address2}</div> : null}
              {addressLine2 ? <div>{addressLine2}</div> : null}
            </div>
          ) : (
            <div className="confirmModal__pickupEmpty">
              No address on file. Add one in your profile so Arta knows where to
              pick up.
            </div>
          )}
        </div>

        <div className="confirmModal__terms">
          <FormCheck
            type="checkbox"
            id="confirm-shipping-agree"
            label="I agree to the"
            checked={isAgreed}
            onChange={(e) => setIsAgreed(e.target.checked)}
          />
          <Link to="/terms-and-conditions" target="_blank">
            Terms and Conditions
          </Link>
        </div>

        <div className="confirmModal__footer">
          <Button
            className="closeBtn"
            variant="secondary"
            onClick={onNo}
          >
            {closeButtonText}
          </Button>
          <button
            type="button"
            className="confirmModal__primary"
            onClick={onYes}
            disabled={!isAgreed}
          >
            {confirmButtonText}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ConfirmShippingPopup;
